import { useCallback, useEffect, useRef } from "react";

/**
 * The interactive layer.
 *
 * All of them write CSS custom properties and let CSS do the animating.
 * Setting a property is a style recalculation the compositor can absorb;
 * driving the same effect through React state would re-render the card on
 * every pointer move.
 */

const fine = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
  !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Moves the specular highlight of `.spot` with the pointer. */
export function useSpotlight() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !fine()) return;

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
      el.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
    };

    el.addEventListener("pointermove", onMove);
    return () => el.removeEventListener("pointermove", onMove);
  }, []);

  return ref;
}

/**
 * Tilts an element toward the pointer and lifts it slightly.
 *
 * Deliberately shallow — 6° and 8° . Anything more and a card of text becomes
 * hard to read while you are pointing at it, which is the moment you most
 * want to read it.
 */
export function useTilt({ max = 6, lift = 6 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !fine()) return;

    let frame = 0;

    const onMove = (e) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        el.style.setProperty("--ry", `${px * max * 2}deg`);
        el.style.setProperty("--rx", `${-py * max}deg`);
        el.style.setProperty("--ty", `${-lift}px`);
        el.style.setProperty("--mx", `${(px + 0.5) * 100}%`);
        el.style.setProperty("--my", `${(py + 0.5) * 100}%`);
      });
    };

    const reset = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      el.style.setProperty("--rx", "0deg");
      el.style.setProperty("--ry", "0deg");
      el.style.setProperty("--ty", "0px");
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", reset);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", reset);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [max, lift]);

  return ref;
}

/**
 * Pulls a button a few pixels toward the pointer before you reach it.
 *
 * The catch is the skew: `.magnet` composes `skewX()` with the translate in
 * one transform, so this hook may only write --dx/--dy and must never set
 * `transform` itself — doing that would drop the lean the moment you hovered.
 *
 * Six pixels of travel, and it releases the moment the pointer leaves. Any
 * further and the button starts running away from the cursor, which is funny
 * once and annoying every time after.
 */
export function useMagnet({ pull = 6, radius = 1.6 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !fine()) return;

    let frame = 0;

    const onMove = (e) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const r = el.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        /* Normalised against half the element, so a wide button and a small
           one pull with the same feel rather than the same pixels. */
        const dx = (e.clientX - cx) / (r.width / 2);
        const dy = (e.clientY - cy) / (r.height / 2);
        const clamp = (v) => Math.max(-1, Math.min(1, v / radius));
        el.style.setProperty("--dx", `${(clamp(dx) * pull).toFixed(2)}px`);
        el.style.setProperty("--dy", `${(clamp(dy) * pull).toFixed(2)}px`);
      });
    };

    const reset = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      el.style.setProperty("--dx", "0px");
      el.style.setProperty("--dy", "0px");
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", reset);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", reset);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [pull, radius]);

  return ref;
}

/**
 * Counts up to a number the first time it is scrolled into view.
 *
 * Non-numeric values ("9+", "2021") are handled by counting the digits and
 * keeping whatever is stuck to them, so the markup stays one string.
 */
export function useCountUp(value, { duration = 1100 } = {}) {
  const ref = useRef(null);

  const run = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const digits = String(value).match(/[\d.]+/);
    if (!digits) {
      el.textContent = String(value);
      return;
    }

    const target = Number(digits[0]);
    const suffix = String(value).slice(digits.index + digits[0].length);
    const prefix = String(value).slice(0, digits.index);

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.textContent = String(value);
      return;
    }

    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      /* Ease out: fast at first, then settling — a linear count reads like a
         loading bar rather than an arrival. */
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = `${prefix}${Math.round(target * eased)}${suffix}`;
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value, duration]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          run();
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.4 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [run]);

  return ref;
}
